import { AnalysisMode, AnalysisConfig, DEFAULT_CONFIGS } from '@/types/trading-analysis-types';
import { BasicAnalyzer } from './analyzers/BasicAnalyzer';
import { IntermediateAnalyzer } from './analyzers/IntermediateAnalyzer';
import { AdvancedAnalyzer } from './analyzers/AdvancedAnalyzer';

export class AnalyzerFactory {
  /**
   * Create analyzer instance based on mode
   */
  static createAnalyzer(mode: AnalysisMode, customConfig?: Partial<AnalysisConfig>) {
    const baseConfig = DEFAULT_CONFIGS[mode];
    const config = customConfig ? { ...baseConfig, ...customConfig } : baseConfig;

    switch (mode) {
      case 'basic':
        return new BasicAnalyzer(config);
      
      case 'intermediate':
        return new IntermediateAnalyzer(config);
      
      case 'advanced':
        return new AdvancedAnalyzer(config);
      
      default:
        throw new Error(`Unsupported analysis mode: ${mode}`);
    }
  }

  /**
   * Validate configuration for specific mode
   */
  static validateConfig(mode: AnalysisMode, config: Partial<AnalysisConfig>): string[] {
    const errors: string[] = [];

    // Common validations
    if (config.minPatternLength && config.minPatternLength < 1) {
      errors.push('Minimum pattern length must be at least 1');
    }

    if (config.maxPatternLength && config.maxPatternLength > 20) {
      errors.push('Maximum pattern length cannot exceed 20');
    }

    if (config.minPatternLength && config.maxPatternLength && 
        config.minPatternLength > config.maxPatternLength) {
      errors.push('Minimum pattern length cannot be greater than maximum');
    }

    if (config.minFrequency && config.minFrequency < 1) {
      errors.push('Minimum frequency must be at least 1');
    }

    // Mode-specific validations
    if (mode === 'intermediate' || mode === 'advanced') {
      if (config.similarityThreshold && 
          (config.similarityThreshold < 0 || config.similarityThreshold > 1)) {
        errors.push('Similarity threshold must be between 0 and 1');
      }
    }

    if (mode === 'advanced') {
      if (config.confidenceLevel && 
          (config.confidenceLevel < 0.5 || config.confidenceLevel > 0.99)) {
        errors.push('Confidence level must be between 0.5 and 0.99');
      }

      if (config.kFoldSplits && config.kFoldSplits < 2) {
        errors.push('K-fold splits must be at least 2');
      }
    }

    return errors;
  }

  /**
   * Get available modes with descriptions
   */
  static getModeDescriptions() {
    return {
      basic: {
        name: 'Basic Analysis',
        description: 'Exact pattern matching with simple confidence scoring',
        features: ['Exact pattern matching', 'Rule-based confidence', 'Frequency weighting'],
        performance: 'Fast, suitable for simple patterns'
      },
      intermediate: {
        name: 'Intermediate Analysis',
        description: 'Fuzzy matching with enhanced weights and similarity scoring',
        features: ['Fuzzy pattern matching', 'Enhanced weights', 'Similarity scoring', 'Recency factors'],
        performance: 'Balanced speed and accuracy'
      },
      advanced: {
        name: 'Advanced Analysis',
        description: 'Statistical validation with cross-validation and ensemble methods',
        features: ['Statistical validation', 'Cross-validation', 'Ensemble methods', 'Anomaly detection'],
        performance: 'Most accurate, slower processing'
      }
    };
  }

  /**
   * Get recommended mode based on data characteristics
   */
  static getRecommendedMode(dataSize: number, complexity: 'low' | 'medium' | 'high'): AnalysisMode {
    if (dataSize < 100) {
      return 'basic';
    }

    if (dataSize < 1000 && complexity === 'low') {
      return 'intermediate';
    }

    if (complexity === 'high' || dataSize >= 1000) {
      return 'advanced';
    }

    return 'intermediate';
  }
}